import { CodeBlock, EmptyState, QuotaBar } from '@myog/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { API_URL, api, apiFailure } from '../lib/api-client.js';

/**
 * Page Overview.
 *
 * Hiérarchie voulue (docs/DESIGN.md §5.2) : le quota d'abord — c'est la
 * question n°1 de l'utilisateur. Puis le snippet prêt à copier, qui est
 * l'action suivante d'un nouvel inscrit. Le graphe ne vient qu'après.
 */

export function Overview() {
  const usage = useQuery({
    queryKey: ['usage', '30d'],
    queryFn: async () => {
      const response = await api.v1.analytics.usage.$get({ query: { range: '30d' } });
      if (!response.ok) throw await apiFailure(response, 'Impossible de charger la consommation.');
      return response.json();
    },
    // Le compteur est flushé toutes les 60 s côté serveur : rafraîchir plus
    // souvent afficherait la même valeur (voir ADR-005).
    refetchInterval: 60_000,
  });

  const keys = useQuery({
    queryKey: ['keys'],
    queryFn: async () => {
      const response = await api.v1.keys.$get();
      if (!response.ok) throw await apiFailure(response, 'Impossible de charger les clés.');
      return response.json();
    },
  });

  if (usage.isPending) {
    return <OverviewSkeleton />;
  }

  if (usage.isError) {
    return (
      <EmptyState
        title="Chargement impossible"
        description={usage.error.message}
        action={
          <button
            type="button"
            onClick={() => usage.refetch()}
            className="text-sm font-medium text-accent hover:underline"
          >
            Réessayer
          </button>
        }
      />
    );
  }

  const data = usage.data;
  const publicKey = keys.data?.keys.find((k) => k.type === 'public' && k.isActive);
  const hasTraffic = data.totals.requests > 0;

  return (
    <div className="space-y-8">
      <section
        aria-labelledby="quota-heading"
        className="rounded-lg border border-border bg-surface p-6"
      >
        <h2 id="quota-heading" className="mb-4 text-lg font-semibold text-text">
          Consommation du mois
        </h2>
        <QuotaBar used={data.used} limit={data.monthlyLimit} />
        {/*
          Préciser « générées » n'est pas un détail de formulation : c'est la
          différence entre ce compteur et celui, bien plus élevé, des requêtes
          affichées juste en dessous. Sans cette phrase, un client dont le cache
          absorbe 95 % du trafic croit à un bug de comptage.
        */}
        <p className="mt-3 text-xs text-text-subtle">
          Seules les images <strong className="font-medium text-text-muted">générées</strong> sont
          décomptées. Celles servies depuis le cache sont illimitées.
        </p>
        <p className="mt-2 text-xs text-text-subtle">
          Période {data.periodMonth} · plan {data.plan} · mis à jour il y a moins d'une minute
        </p>
      </section>

      {publicKey ? (
        <section aria-labelledby="snippet-heading">
          <h2 id="snippet-heading" className="mb-3 text-lg font-semibold text-text">
            Votre intégration
          </h2>
          <p className="mb-3 text-sm text-text-muted">
            Collez cette ligne dans le <code className="font-mono">&lt;head&gt;</code> de vos pages.
            Remplacez le titre par celui de la page courante, et{' '}
            <code className="font-mono">VOTRE_CLE_PUBLIQUE</code> par la clé{' '}
            <span className="font-mono">{publicKey.prefix}…</span> copiée à sa création.
          </p>
          {/*
           * Marqueur explicite, et non le préfixe suivi de points de
           * suspension. La valeur brute n'est jamais stockée : ce snippet ne
           * PEUT pas être fonctionnel. Afficher `myog_pk_9NHN...` donnait
           * pourtant l'illusion du contraire — c'était copié tel quel, et
           * l'API répondait « clé invalide » sans que la cause saute aux yeux.
           */}
          <CodeBlock
            language="html"
            code={`<meta property="og:image" content="${API_URL}/v1/og?title=Titre+de+la+page&template=gradient&key=VOTRE_CLE_PUBLIQUE" />`}
          />
        </section>
      ) : (
        <EmptyState
          title="Créez votre première clé API"
          description="Une clé publique suffit pour commencer : elle s'utilise directement dans vos balises meta."
          action={
            /*
             * Le libellé annonce une navigation, pas une création — cette
             * action mène à la page des clés, où le bouton porte le même mot.
             * Promettre « Créer une clé » ici donnait l'impression d'un échec :
             * l'utilisateur arrivait sur une liste vide en croyant avoir créé
             * quelque chose.
             *
             * `Link` et non `<a>` : une ancre rechargerait toute l'application,
             * viderait le cache de requêtes et rejouerait la vérification de
             * session pour un simple changement d'onglet.
             */
            <Link
              to="/keys"
              className="inline-flex h-11 items-center rounded-md bg-accent px-4 text-sm font-medium text-accent-fg hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            >
              Aller aux clés API
            </Link>
          }
        />
      )}

      <section aria-labelledby="chart-heading">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 id="chart-heading" className="text-lg font-semibold text-text">
            30 derniers jours
          </h2>
          {hasTraffic ? (
            <p className="text-sm text-text-muted">
              Taux de cache <span className="font-mono text-success">{data.cacheHitRate} %</span>
            </p>
          ) : null}
        </div>

        {hasTraffic ? (
          <>
            <div className="h-64 w-full rounded-lg border border-border bg-surface p-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={[...data.series]}>
                  <XAxis
                    dataKey="date"
                    stroke="var(--color-text-subtle)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value: string) => value.slice(5)}
                  />
                  <YAxis
                    stroke="var(--color-text-subtle)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    width={44}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--color-surface-raised)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--color-text)',
                      fontSize: 13,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="cacheHits"
                    name="Servies depuis le cache"
                    stackId="1"
                    stroke="var(--color-success)"
                    fill="var(--color-success)"
                    fillOpacity={0.12}
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="renders"
                    name="Générées"
                    stackId="1"
                    stroke="var(--color-accent)"
                    fill="var(--color-accent)"
                    fillOpacity={0.18}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Un graphique n'est pas lisible par un lecteur d'écran : on
                double systématiquement par un tableau. WCAG 1.1.1. */}
            <table className="sr-only">
              <caption>Consommation quotidienne des 30 derniers jours</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Requêtes</th>
                  <th scope="col">Depuis le cache</th>
                  <th scope="col">Générées</th>
                  <th scope="col">Erreurs</th>
                </tr>
              </thead>
              <tbody>
                {data.series.map((point) => (
                  <tr key={point.date}>
                    <th scope="row">{point.date}</th>
                    <td>{point.requests}</td>
                    <td>{point.cacheHits}</td>
                    <td>{point.renders}</td>
                    <td>{point.errors}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <EmptyState
            title="Aucune image générée pour l'instant"
            description="Dès que vos pages seront partagées, la consommation apparaîtra ici."
          />
        )}
      </section>
    </div>
  );
}

/** Chargement par blocs plutôt qu'un spinner plein écran (docs/DESIGN.md §3.2). */
function OverviewSkeleton() {
  return (
    <div className="space-y-8" aria-busy="true" aria-label="Chargement des métriques">
      <div className="h-32 animate-pulse rounded-lg bg-surface motion-reduce:animate-none" />
      <div className="h-24 animate-pulse rounded-lg bg-surface motion-reduce:animate-none" />
      <div className="h-64 animate-pulse rounded-lg bg-surface motion-reduce:animate-none" />
    </div>
  );
}
